// AI referral classification (#485).
//
// Nothing in PostHog separates AI-assistant referrals from other traffic, so we
// cannot tell whether AI search sends visitors, or whether those visitors
// convert. The only AI-specific surface on the site is /ai-information.
//
// The referrer data already exists. What was missing was a way to say "this
// visit came from an AI assistant" in a form a query can filter on.
//
// The property is `ai_referrer`, and it holds the assistant's name when the
// visit came from one: 'chatgpt', 'perplexity', 'gemini', 'claude', 'copilot'.
// It is absent (not empty) when the visit did not come from an AI assistant,
// so a query can test it either way.
//
// Two ways an AI assistant sends someone:
//   1. A referrer: the assistant links to the site, the browser sets
//      document.referrer.
//   2. A UTM: ChatGPT appends `?utm_source=chatgpt.com` to links it
//      generates, so the source arrives in the query string and the referrer
//      may be empty.
//
// Both are handled. The referrer wins when both are present, because the
// referrer says where the click actually came from.
//

/**
 * Hosts that identify an AI assistant, mapped to the assistant's name.
 *
 * Matched on the hostname, so `chatgpt.com`, `chat.openai.com` and
 * `chatgpt-com` all resolve to 'chatgpt'.
 */
const AI_REFERRER_HOSTS = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'chatgpt-com': 'chatgpt',
  'openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'www.perplexity.ai': 'perplexity',
  'perplexity-com': 'perplexity',
  'gemini.google.com': 'gemini',
  'bard.google.com': 'gemini',
  'claude.ai': 'claude',
  'anthropic.com': 'claude',
  'copilot.microsoft.com': 'copilot',
  'm365.cloud.microsoft.com': 'copilot',
  'copilot.com': 'copilot',
};

/**
 * UTM sources that identify an AI assistant. ChatGPT appends
 * `utm_source=chatgpt.com` to the links it generates.
 */
const AI_UTM_SOURCES = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'chatgpt': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'perplexity': 'perplexity',
  'gemini.google.com': 'gemini',
  'claude.ai': 'claude',
  'copilot.microsoft.com': 'copilot',
};

/**
 * The assistant a referrer or utm_source names, or null.
 *
 * Accepts a bare hostname, a full URL, or a hostname with a port. Case is
 * ignored, and a leading `www.` is ignored.
 *
 * `google.com` is deliberately NOT in the map. Google Search sends far more
 * traffic than Gemini, and mapping the whole domain to 'gemini' would report
 * ordinary search traffic as AI referral. `gemini.google.com` and
 * `bard.google.com` are specific enough.
 */
function classifyAIReferrer(referrerOrSource) {
  if (!referrerOrSource || typeof referrerOrSource !== 'string') return null;
  const value = referrerOrSource.trim().toLowerCase();
  if (!value) return null;

  // A full URL: take its hostname.
  let host = value;
  if (value.includes('://')) {
    try {
      host = new URL(value).hostname;
    } catch {
      return null;
    }
  } else {
    // Strip a path, query or port if one was passed.
    host = value.split(/[/?#]/)[0].replace(/:\d+$/, '');
  }

  if (AI_REFERRER_HOSTS[host]) return AI_REFERRER_HOSTS[host];

  // `www.` is not in the map for most entries, so strip it and retry.
  const withoutWww = host.replace(/^www\./, '');
  if (AI_REFERRER_HOSTS[withoutWww]) return AI_REFERRER_HOSTS[withoutWww];

  // Not a host we know, but it might be a utm_source.
  if (AI_UTM_SOURCES[host]) return AI_UTM_SOURCES[host];
  if (AI_UTM_SOURCES[withoutWww]) return AI_UTM_SOURCES[withoutWww];

  return null;
}

/**
 * The assistant for a visit, from the referrer and the utm_source together.
 *
 * The referrer wins when both are present. When the referrer is absent (the
 * visitor typed the URL, or the referrer was stripped) the utm_source is used,
 * because that is how ChatGPT tags its links.
 */
function classifyAIVisit({ referrer, utmSource } = {}) {
  const fromReferrer = classifyAIReferrer(referrer);
  if (fromReferrer) return fromReferrer;
  return classifyAIReferrer(utmSource);
}

// AUTO-GENERATED from shared/aiReferrer.js
// Run `node scripts/sync-ai-referrer.js` to regenerate.

export {
  classifyAIReferrer,
  classifyAIVisit,
  AI_REFERRER_HOSTS,
  AI_UTM_SOURCES,
};
